import type { fnGetFilteredProductLinks, ProductLink } from '@atlassian/global-shortcuts';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/types';

import {
	type Collaborator,
	INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS,
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedApsKey,
	type InvitedAppShortcutAllowedTargetAppKey,
	type InvitedAppShortcutRecommendedAppInput,
} from '../../../common/types';
import {
	getAppKeyByApsKey,
	normaliseRecommendedApp,
} from '../../../common/utils/invited-app-shortcuts-utils';
import type { CollaboratorsWithProducts } from '../../collaborators-in-products';
import {
	type CachedAppState,
	type CachedState,
	type ExperimentDetails,
	FirstActiveSpecialValues,
	type FullContext,
	IneligibilityReason,
	type IneligibleExperimentDetails,
	type InvitedAppDetails,
	type ProductInState,
	ProductsInState,
	type StoredTimestamp,
	type Trait,
	type Traits,
} from '../types';

import { enqueueUpdate } from './cache-update-queue';
import { getConfig } from './config';

export type ResolvedCacheDetails = {
	eligibility: ExperimentDetails;
	state?: CachedState;
};

export const resolveFromCache = ({
	sourceProduct,
	targetProducts,
	userId,
	cache,
}: Pick<
	FullContext,
	'sourceProduct' | 'targetProducts' | 'cache' | 'userId'
>): ResolvedCacheDetails => {
	if (targetProducts.length === 0) {
		return { eligibility: ineligible(IneligibilityReason.NO_TARGET_PRODUCTS) };
	}

	const cached = cache.read({ ssr: true });

	if (userId && cached && cached.uid && userId !== cached.uid) {
		// Extra protection: clear cache asynchronously in the background, ignoring errors.
		// The hook will then fill the cache for the correct, current user.
		Promise.resolve()
			.then(() => enqueueUpdate(cache, () => ({ uid: cached.uid })))
			.catch(() => {});

		return { eligibility: ineligible(IneligibilityReason.CACHE_EMPTY_USER_MISMATCH) };
	}

	if (!(cached && cached.apps)) {
		return { eligibility: ineligible(IneligibilityReason.CACHE_EMPTY) };
	}

	const resolvedDetails = resolveExperimentDetails({ sourceProduct, targetProducts }, cached);

	return { eligibility: resolvedDetails, state: cached };
};

export const resolveExperimentDetails = (
	{
		sourceProduct,
		targetProducts,
		timestamp = Date.now(),
	}: Pick<FullContext, 'sourceProduct' | 'targetProducts'> & { timestamp?: number },
	cachedState: CachedState,
): ExperimentDetails => {
	if (targetProducts.length === 0) {
		return ineligible(IneligibilityReason.NO_TARGET_PRODUCTS);
	}

	let invitedApps: ExperimentDetails['invitedApps'] = {};
	let hasAccessToAtLeastOneTarget: boolean | null = null;
	let atLeastOneTargetVisible: boolean | null = null;
	let atLeastOneTargetInactive = false;
	const ignoreVisibility = shouldIgnoreVisibility({ sourceProduct });

	for (const targetProduct of targetProducts) {
		const cachedApp = cachedState.apps?.[targetProduct];
		if (!cachedApp) {
			continue;
		}

		if (cachedApp.access != null) {
			hasAccessToAtLeastOneTarget = hasAccessToAtLeastOneTarget || cachedApp.access;
		}

		if (cachedApp.visible != null) {
			atLeastOneTargetVisible = atLeastOneTargetVisible || cachedApp.visible;
		}

		if (
			cachedApp.access &&
			(cachedApp.visible || ignoreVisibility) &&
			cachedApp.firstActive === FirstActiveSpecialValues.INACTIVE
		) {
			atLeastOneTargetInactive = true;

			invitedApps[targetProduct] = {
				eligible: true,
				collaborators: Array.from({ length: cachedApp.collaboratorCount ?? 0 }).map(
					(_, index) => cachedApp.collaboratorDetails?.[index] ?? null,
				),
				firstImpressionAt: cachedApp?.firstImpressionAt ?? null,
			};
		}
	}

	if (
		hasAccessToAtLeastOneTarget === null ||
		(!ignoreVisibility && atLeastOneTargetVisible === null)
	) {
		return ineligible(IneligibilityReason.CACHE_EMPTY);
	}

	if (!hasAccessToAtLeastOneTarget) {
		return ineligible(IneligibilityReason.NO_ACCESS_TO_TARGET_PRODUCTS);
	}

	if (!atLeastOneTargetVisible && !ignoreVisibility) {
		return ineligible(IneligibilityReason.TARGET_PRODUCTS_NOT_VISIBLE);
	}

	if (!cachedState.firstActiveUpdatedTimestamp) {
		return ineligible(IneligibilityReason.CACHE_EMPTY);
	}

	if (
		!(timestamp <= cachedState.firstActiveUpdatedTimestamp + getConfig().CACHE_TIME_UNTIL_EXPIRY)
	) {
		return ineligible(IneligibilityReason.CACHE_EXPIRED);
	}

	if (sourceProduct !== 'home') {
		const firstActiveInSourceProduct = cachedState.apps?.[sourceProduct]?.firstActive;
		if (!firstActiveInSourceProduct) {
			return ineligible(IneligibilityReason.CACHE_EMPTY);
		}
		if (
			timestamp - firstActiveInSourceProduct <
			getConfig().TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE
		) {
			return ineligible(IneligibilityReason.SOURCE_PRODUCT_TOO_NEW);
		}
	}

	if (!atLeastOneTargetInactive) {
		return ineligible(IneligibilityReason.TARGET_PRODUCTS_ALL_ACTIVE);
	}
	return {
		ineligibilityReason: null,
		invitedApps,
	};
};

/**
 * Build a `ResolvedCacheDetails` for `mode: 'productionisation'` from a
 * caller-supplied list of recommended target apps (typically derived from
 * `InvitedAppShortcutsRecommendationsProvider`). Returns `NO_RECOMMENDED_APPS`
 * when `recommendedApps` is empty.
 *
 * Notes:
 * - Only recommended apps appear in `invitedApps`; non-recommended target
 *   products are absent. Consumers must iterate `targetProducts` themselves
 *   when they need to enumerate all possible apps.
 * - `collaborators` / `firstImpressionAt` are intentionally left `undefined`
 *   in productionisation; they are owned by Relay (collaborators) and Post
 *   Office (impression state) respectively. See `InvitedAppDetails`.
 * - `invitedApps` is keyed by the **parsed product**, never by the raw GRS
 *   `entityId`, which since PUMA-2245 may be variant-encoded. Callers passing
 *   bare product keys still work; they normalise to `variant: 'none'`.
 */
export const resolveFromRecommendations = ({
	recommendedApps,
}: {
	recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedAppInput>;
}): ResolvedCacheDetails => {
	if (recommendedApps.length === 0) {
		return { eligibility: ineligible(IneligibilityReason.NO_RECOMMENDED_APPS) };
	}

	const invitedApps: Partial<Record<InvitedAppShortcutAllowedTargetAppKey, InvitedAppDetails>> = {};
	for (const app of recommendedApps) {
		const { product, variant } = normaliseRecommendedApp(app);
		invitedApps[product] = { eligible: true, variant };
	}

	return { eligibility: { ineligibilityReason: null, invitedApps } };
};

export const ineligible = (reason: IneligibilityReason): IneligibleExperimentDetails => ({
	ineligibilityReason: reason,
});

export const isFirstActiveExpiringSoonNeedsRefresh = (
	cachedState: CachedState | undefined,
): boolean =>
	!cachedState?.firstActiveUpdatedTimestamp ||
	Date.now() > cachedState.firstActiveUpdatedTimestamp + getConfig().CACHE_TIME_UNTIL_REFRESH;

export type AccessMap = {
	[target in ProductInState]?: boolean;
};

export type VisibilityMap = {
	[target in ProductInState]?: boolean;
};

export type DynamicAppEligibility = {
	access: AccessMap;
	visibility: VisibilityMap;
};

export const updateCachedEligibility = async ({
	access: accessMap,
	visibility: visibilityMap,
	traits,
	userId,
	cache,
}: {
	access: AccessMap;
	traits: Traits;
	visibility: VisibilityMap;
} & Pick<FullContext, 'cache' | 'userId'>): Promise<CachedState> =>
	enqueueUpdate(cache, (prev) => {
		const draft = { ...prev, uid: userId, apps: { ...prev?.apps } };
		const traitsByName = groupTraitsByName(traits);
		for (const product of ProductsInState) {
			const appAccess = accessMap[product];
			const appVisibility = visibilityMap[product];
			const traitValue = getProductFirstActiveTraitValue(product, traitsByName);
			if (traitValue !== FIRST_ACTIVE_NOT_APPLICABLE) {
				const firstActive = traitValue ? toStoredTimestamp(traitValue) : null;
				const draftApp: CachedAppState = {
					...draft.apps[product],
				};
				if (firstActive) {
					draftApp.firstActive = firstActive;
				} else if (appAccess === true) {
					draftApp.firstActive = FirstActiveSpecialValues.INACTIVE;
				}
				if (appAccess !== undefined) {
					draftApp.access = appAccess;
				}
				if (appVisibility !== undefined) {
					draftApp.visible = appVisibility;
				}
				draft.apps[product] = draftApp;
			}
		}
		draft.firstActiveUpdatedTimestamp = Date.now();
		return draft;
	});

const FIRST_ACTIVE_NOT_APPLICABLE = Symbol('FIRST_ACTIVE_NOT_APPLICABLE');

const getProductFirstActiveTraitValue = (
	product: ProductInState,
	traitsByName: TraitsByName,
): Trait['value'] | typeof FIRST_ACTIVE_NOT_APPLICABLE | undefined => {
	switch (product) {
		case 'jira':
			return traitsByName['jira_first_active'] || traitsByName['jira-software_first_active'];
		case 'confluence':
			return traitsByName['confluence_first_active'];
		case 'home':
			return FIRST_ACTIVE_NOT_APPLICABLE;
		default:
			product satisfies never; // Should never get here - handle all products above!
	}
};

const shouldIgnoreVisibility = ({ sourceProduct }: Pick<FullContext, 'sourceProduct'>) =>
	sourceProduct === 'confluence';

export const anyTargetProductEligible = ({
	sourceProduct,
	targetProducts,
	dynamicEligibility,
}: Pick<FullContext, 'sourceProduct' | 'targetProducts'> & {
	dynamicEligibility: DynamicAppEligibility | null;
}): boolean =>
	!!dynamicEligibility &&
	targetProducts.some(
		(product) =>
			dynamicEligibility.access[product] === true &&
			(dynamicEligibility.visibility[product] === true ||
				shouldIgnoreVisibility({ sourceProduct })),
	);

export type GetAppAccessInput = {
	getFilteredProductLinks: fnGetFilteredProductLinks;
	limitToOnlyApsKeys?: InvitedAppShortcutAllowedApsKey[];
	visibleMenuIds: ReadonlyArray<string>;
};
/**
 * Get the app access from the product links filter
 * @param GetAppAccessInput - The input to get the app access from the product links filter
 * @returns The app access map, or undefined if the filter hasn't loaded yet
 */
export const getAppAccessFromProductLinksFilter = ({
	getFilteredProductLinks,
	limitToOnlyApsKeys = [...INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS],
}: Pick<GetAppAccessInput, 'getFilteredProductLinks' | 'limitToOnlyApsKeys'>):
	| AccessMap
	| undefined => {
	const access: AccessMap = {};
	let atLeastOneComplete = false;
	for (const apsKey of limitToOnlyApsKeys) {
		const app = getAppKeyByApsKey(apsKey as InvitedAppShortcutAllowedApsKey);
		const { links, status } = getFilteredProductLinks(apsKey);
		if (status === 'complete') {
			atLeastOneComplete = true;
			const hasAccess = links.length > 0;
			access[app] = hasAccess;
		}
	}
	return atLeastOneComplete ? access : undefined;
};

export type GetAppAccessByShortcutsInput = {
	limitToOnlyApsKeys?: InvitedAppShortcutAllowedApsKey[];
	shortcuts: ShortcutsConfig;
	visibleMenuIds: ReadonlyArray<string>;
};

const menuIdToApsKey: { [key in string]: InvitedAppShortcutAllowedApsKey } = {
	// Jira to Confluence
	'jira.sidebar.globalApp.confluence': 'CONFLUENCE',
	// Confluence to Jira
	jiraShortcut: 'JIRA_SOFTWARE',
};

/**
 * Get the app access by the shortcuts
 * @param shortcuts - The shortcuts to get the app access for
 * @param limitToOnlyApsKeys - The APS keys to limit the access to
 * @returns The app access map
 */
export const getAppAccessFromShortcuts = ({
	shortcuts,
	limitToOnlyApsKeys = [...INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS],
}: Pick<GetAppAccessByShortcutsInput, 'shortcuts' | 'limitToOnlyApsKeys'>): AccessMap => {
	const access: AccessMap = Object.values(shortcuts).reduce<AccessMap>((acc, shortcut) => {
		if (shortcut.type === 'link' && shortcut.apsKey) {
			const { apsKey, url } = shortcut;
			// if the url is valid then we assume the user has access to the product
			if (url && limitToOnlyApsKeys.includes(apsKey as InvitedAppShortcutAllowedApsKey)) {
				try {
					const appKey = getAppKeyByApsKey(apsKey as InvitedAppShortcutAllowedApsKey);
					acc[appKey] = true;
					// eslint-disable-next-line no-unused-vars
				} catch (_) {
					// if fails to get the app key then it's not an allowed app, skip it
				}
			}
		}

		return acc;
	}, {});
	return applyDefaultsForApsKeys(access, limitToOnlyApsKeys, false);
};

const applyDefaultsForApsKeys = <AppMap extends { [app in ProductInState]?: unknown }>(
	appMap: AppMap,
	apsKeys: InvitedAppShortcutAllowedApsKey[],
	defaultValue: AppMap[keyof AppMap],
): AppMap => {
	const result: AppMap = { ...appMap };
	for (let apsKey of apsKeys) {
		const app = getAppKeyByApsKey(apsKey);
		if (result[app] === undefined) {
			result[app] = defaultValue;
		}
	}
	return result;
};

export const getVisibilityFromVisibleMenuIds = ({
	visibleMenuIds,
	limitToOnlyApsKeys: apsKeys = [...INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS],
}: {
	limitToOnlyApsKeys?: InvitedAppShortcutAllowedApsKey[];
	visibleMenuIds: ReadonlyArray<string>;
}): VisibilityMap => {
	const visibleApsKeys = new Set(
		visibleMenuIds.map((menuId) => menuIdToApsKey[menuId]).filter(Boolean),
	);

	const visibilityMap: VisibilityMap = {};
	for (const apsKey of apsKeys) {
		const appKey = getAppKeyByApsKey(apsKey as InvitedAppShortcutAllowedApsKey);
		visibilityMap[appKey] = visibleApsKeys.has(apsKey);
	}
	return visibilityMap;
};

export const updateAccessAndVisibility = async ({
	access: accessMap,
	visibility: visibilityMap,
	cache,
}: {
	access: AccessMap;
	visibility: VisibilityMap;
} & Pick<FullContext, 'cache'>): Promise<CachedState> =>
	enqueueUpdate(cache, (prev) => {
		let updated = false;

		const draft = { ...prev, apps: { ...prev?.apps } };
		for (const app of ProductsInState) {
			const appAccess = accessMap[app];
			const appVisibility = visibilityMap[app];
			const prevApp = draft.apps[app] ?? {};
			if (
				(appAccess !== undefined && appAccess !== prevApp.access) ||
				(appVisibility !== undefined && appVisibility !== prevApp.visible)
			) {
				const draftApp: CachedAppState = {
					...prevApp,
				};
				if (appAccess !== undefined) {
					draftApp.access = appAccess;
				}
				if (appVisibility !== undefined) {
					draftApp.visible = appVisibility;
				}
				draft.apps[app] = draftApp;
				updated = true;
			}
		}

		return updated ? draft : null;
	});

export const updateFirstImpression = async ({
	productsSeen,
	seenTimestamp = Date.now(),
	cache,
}: Pick<FullContext, 'cache'> & {
	productsSeen: InvitedAppShortcutAllowedTargetAppKey[];
	seenTimestamp?: number;
}): Promise<CachedState> =>
	enqueueUpdate(cache, (prev) => {
		const draft = { ...prev, apps: { ...prev?.apps } };
		let updated = false;
		for (const product of productsSeen) {
			const prevApp = draft.apps[product];
			if (!prevApp?.firstImpressionAt) {
				updated = true;
				draft.apps[product] = {
					...prevApp,
					firstImpressionAt: seenTimestamp,
				};
			}
		}

		return updated ? draft : null;
	});

export const updateActivation = async ({
	cache,
	product,
	timestamp = Date.now(),
}: Pick<FullContext, 'cache'> & {
	product: InvitedAppShortcutAllowedTargetAppKey;
	timestamp?: number;
}): Promise<CachedState> =>
	enqueueUpdate(cache, (prev) => {
		const draft = { ...prev, apps: { ...prev?.apps } };
		draft.apps[product] = {
			...draft.apps[product],
			firstActive: timestamp,
		};
		return draft;
	});

export const updateCollaborators = async ({
	products,
	collaboratorsWithProducts,
	cache,
}: Pick<FullContext, 'cache'> & {
	collaboratorsWithProducts: CollaboratorsWithProducts;
	products: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
}): Promise<CachedState> =>
	enqueueUpdate(cache, (prev) => {
		const collaboratorsByProduct = groupCollaboratorsByProduct(collaboratorsWithProducts);

		const draft = { ...prev, apps: { ...prev?.apps } };
		for (const product of products) {
			const collabs = collaboratorsByProduct[product] ?? [];
			draft.apps[product] = {
				...draft.apps[product],

				// We display:
				// - 1x avatar for 1 collaborator
				// - 2x avatars for exactly 2 collaborators
				// - 1x avatar + number of remaining collaborators for 3+
				// So store exactly as much detailed data as we need to display:
				collaboratorDetails: collabs
					.slice(0, collabs.length === 2 ? 2 : 1)
					.map((c) => ({ avatar: c.avatar })),
				collaboratorCount: collabs.length,
			};
		}

		return draft;
	});

export type CachedProductLinks = Partial<
	Record<InvitedAppShortcutAllowedTargetAppKey, ProductLink[]>
>;

export const updateHydratedProductLinks = async ({
	productLinks,
	cache,
}: Pick<FullContext, 'cache'> & {
	productLinks: CachedProductLinks;
}): Promise<CachedState> =>
	enqueueUpdate(cache, (prev) => {
		if (Object.keys(productLinks).length === 0) {
			return null;
		}
		const draft = { ...prev, apps: { ...prev?.apps } };
		let updated = false;

		for (const product of INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS) {
			const links = productLinks[product];
			if (!links) {
				continue;
			}

			const prevLinks = draft.apps[product]?.productLinks;
			if (!areProductLinksEqual(prevLinks, links)) {
				draft.apps[product] = {
					...draft.apps[product],
					productLinks: links,
				};
				updated = true;
			}
		}

		return updated ? draft : null;
	});

const areProductLinksEqual = (
	prevLinks: ProductLink[] | undefined,
	nextLinks: ProductLink[],
): boolean => {
	if (!prevLinks) {
		return false;
	}
	if (prevLinks.length !== nextLinks.length) {
		return false;
	}

	const prevUrls = new Set(prevLinks.map((link) => link?.url ?? ''));
	const nextUrls = new Set(nextLinks.map((link) => link?.url ?? ''));

	if (prevUrls.size !== nextUrls.size) {
		return false;
	}

	for (const url of prevUrls) {
		if (!nextUrls.has(url)) {
			return false;
		}
	}

	return true;
};

/**
 * Get the cached apps from the cache
 * @param cache - The cache to get the apps from
 * @returns The cached apps, or an empty object if the cache is empty or the cookie value is malformed/truncated
 */
const getCachedApps = ({ cache }: Pick<FullContext, 'cache'>): NonNullable<CachedState['apps']> => {
	try {
		const cachedState = cache.read({ ssr: true });

		return cachedState?.apps ?? {};
	} catch {
		// During SSR, if the cookie value is malformed/truncated, JSON.parse will throw.
		// Catch the error and treat it as a cache miss, returning an empty product links map.
		return {};
	}
};

export const getCachedProductLinks = ({
	cache,
}: Pick<FullContext, 'cache'>): CachedProductLinks => {
	const apps = getCachedApps({ cache });

	if (Object.keys(apps).length === 0) {
		return {};
	}

	const cachedProductLinks: CachedProductLinks = {};
	for (const product of INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS) {
		const productLinks = apps?.[product]?.productLinks;
		if (productLinks) {
			cachedProductLinks[product] = productLinks;
		}
	}

	return cachedProductLinks;
};

type CollaboratorsByProduct = { [key in InvitedAppShortcutAllowedTargetAppKey]?: Collaborator[] };
const groupCollaboratorsByProduct = (
	collaboratorsWithProducts: CollaboratorsWithProducts,
): CollaboratorsByProduct => {
	const result: CollaboratorsByProduct = {};
	for (const cwp of collaboratorsWithProducts) {
		for (const product of cwp.products) {
			if (!result[product]) {
				result[product] = [];
			}
			result[product].push(cwp.collaborator);
		}
	}
	return result;
};

type TraitsByName = Record<Trait['name'], Trait['value']>;
const groupTraitsByName = (traits: Traits) => {
	const traitsByName: TraitsByName = {};
	for (const trait of traits) {
		traitsByName[trait.name] = trait.value;
	}
	return traitsByName;
};

export const toStoredTimestamp = (traitValue: Traits[number]['value']): StoredTimestamp | null =>
	(typeof traitValue === 'string' && +new Date(traitValue)) || null;
